import React, { useMemo, useState } from 'react'
import { cpf, cnpj } from 'cpf-cnpj-validator'
import { BRAZILIAN_STATES } from 'src/constants/brazilianStates'
import { Button, Divider, Input, InputCheck, SelectInput } from 'src/components'
import { FarmDto, UpdateFarmDTO } from 'src/dto/farm.dto'
import { useStyles } from './styles'
import { useCreateFarm, useUpdateFarm } from 'src/hooks/api/farm'
import { createPropertySchema, TipoPessoa } from 'src/@types/validations/Property'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useGetCitiesByUf } from 'src/hooks/api/ibge'

interface UpdatePropertiesProps {
  value?: FarmDto
  onClose: () => void
}

function checkRegisterType(documento?: string | null): TipoPessoa {
  if (!documento) return 'PJ'
  const clean = documento.replace(/\D/g, '')
  return clean.length <= 11 ? 'PF' : 'PJ'
}

const UpdateProperties: React.FC<UpdatePropertiesProps> = ({ value, onClose }) => {
  const style = useStyles()

  const { mutateAsync: createFarm, isPending: isCreatingPending } = useCreateFarm()
  const { mutateAsync: updateFarm, isPending: isUpdatingPending } = useUpdateFarm()

  const isLoading = isCreatingPending || isUpdatingPending

  const [registerType, setRegisterType] = useState<TipoPessoa>(() => checkRegisterType(value?.documento))

  const schema = useMemo(() => createPropertySchema(registerType), [registerType])

  const {
    setValue,
    handleSubmit,
    watch,
    clearErrors,
    formState: { errors }
  } = useForm<UpdateFarmDTO>({
    resolver: yupResolver(schema),
    defaultValues: {
      idFazenda: value?.idFazenda ?? undefined,
      descricao: value?.descricao ?? '',
      razao: value?.razao ?? '',
      documento: value?.documento ?? '',
      endereco: value?.endereco ?? '',
      estado: value?.estado ?? 'MT',
      cidade: value?.cidade ?? '',
      proprietario: value?.proprietario ?? ''
    }
  })

  const onSubmit = handleSubmit(async (data) => {
    const { idFazenda, ...rest } = data
    if (!idFazenda) {
      await createFarm(rest, {
        onSuccess: onClose
      })
      return
    }
    await updateFarm(
      {
        idFazenda,
        ...rest
      },
      {
        onSuccess: onClose
      }
    )
  })

  const selectedUf = watch('estado') || 'MT'
  const { data: cities = [] } = useGetCitiesByUf(selectedUf)

  const cityOptions = useMemo(() => {
    return cities.map((city) => ({ value: city.nome, nome: city.nome }))
  }, [cities])

  const handleRegisterTypeChange = (newType: TipoPessoa) => {
    setRegisterType(newType)
    setValue('documento', '')
    clearErrors('documento')
  }

  const isPF = registerType === 'PF'

  return (
    <form onSubmit={onSubmit}>
      <div className={style.updateFarmContainer}>
        <div className={style.checkInputContainer}>
          <InputCheck
            id='pessoa-fisica'
            value='PF'
            label='Pessoa Física'
            checked={isPF}
            onChange={() => handleRegisterTypeChange('PF')}
          />
          <InputCheck
            id='pessoa-juridica'
            value='PJ'
            label='Pessoa Jurídica'
            checked={!isPF}
            onChange={() => handleRegisterTypeChange('PJ')}
          />
        </div>
        <Input
          label='Nome da Fazenda'
          placeholder='Nome da Fazenda'
          value={watch('descricao')}
          error={errors?.descricao?.message}
          className={style.updateFarmInput}
          onChange={(value: string) => setValue('descricao', value)}
        />
        <Input
          label='Razão Social'
          value={watch('razao')}
          placeholder='Razão Social'
          error={errors?.razao?.message}
          className={style.updateFarmInput}
          onChange={(value: string) => setValue('razao', value)}
        />
        <Input
          label={isPF ? 'CPF' : 'CNPJ'}
          maxLength={isPF ? 14 : 18}
          value={watch('documento')}
          placeholder={isPF ? '000.000.000-00' : '00.000.000/0000-00'}
          error={errors?.documento?.message}
          className={style.updateFarmInput}
          onChange={(value: string) => setValue('documento', isPF ? cpf.format(value) : cnpj.format(value))}
        />
        <Input
          label='Endereço'
          placeholder='Endereço'
          value={watch('endereco')}
          className={style.updateFarmInput}
          onChange={(value: string) => setValue('endereco', value)}
        />
        <div className={style.cityUfContainer}>
          <SelectInput
            label='UF'
            options={BRAZILIAN_STATES}
            selectBy='sigla'
            showBy={['sigla', 'nome']}
            value={watch('estado') || ''}
            placeholder='Selecione o estado'
            className={style.updateFarmInputCityUf}
            onChange={(item: { sigla?: string }) => setValue('estado', item?.sigla || '')}
          />
          <SelectInput
            label='Cidade'
            options={cityOptions}
            selectBy='value'
            showBy={['nome']}
            value={watch('cidade') || ''}
            placeholder='Selecione a cidade'
            className={style.updateFarmInputCityUf}
            disabled={!selectedUf}
            onChange={(item: { nome?: string }) => setValue('cidade', item?.nome || '')}
          />
        </div>
        <Input
          label='Proprietário'
          placeholder='Proprietario'
          value={watch('proprietario')}
          className={style.updateFarmInput}
          onPressEnter={onSubmit}
          error={errors?.proprietario?.message}
          onChange={(value: string) => setValue('proprietario', value)}
        />
        <Divider />
        <div className={style.updateFarmButtonContainer}>
          <Button type='button' className={style.updateFarmButtonCancel} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type='submit'
            className={style.updateFarmButton}
            onClick={onSubmit}
            isLoading={isLoading}
            disabled={isLoading}
          >
            Salvar
          </Button>
        </div>
      </div>
    </form>
  )
}

export default UpdateProperties
